"use client";

import Image from "next/image";
import { createPortal } from "react-dom";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import styles from "./gallery-viewer.module.css";

export interface GalleryImage {
  /** Stable key. Falls back to `src` + index when omitted. */
  id?: string;
  src: string;
  alt: string;
}

export interface GalleryViewerProps {
  open: boolean;
  images: GalleryImage[];
  initialIndex?: number;
  onClose: () => void;
  /** Header title shown at the top of the viewer. */
  title?: string;
  /** Close when the user clicks the dimmed backdrop. Default: true. */
  closeOnBackdropClick?: boolean;
}

const SWIPE_THRESHOLD_PX = 50;
const SWIPE_MAX_DURATION_MS = 600;

function clampIndex(i: number, total: number): number {
  if (!Number.isFinite(i) || total <= 0) return 0;
  if (i < 0) return 0;
  if (i >= total) return total - 1;
  return i;
}

/**
 * A single stage image that owns its own load state. Rendered with a per-slide
 * `key`, so it remounts on navigation — that resets the spinner cleanly (no
 * cross-slide race) and lets us handle images the browser already has cached
 * (warmed by the source-page thumbnail or the neighbour preload). For a cached
 * image the native `load` event never fires after mount, so we read
 * `img.complete` via the ref and mark it loaded immediately.
 */
function StageImage({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);

  const measureRef = useCallback((node: HTMLImageElement | null) => {
    if (node && node.complete && node.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <>
      {!loaded && (
        <div
          className={styles.spinner}
          role="status"
          aria-live="polite"
          aria-label="Loading image"
        >
          <span className={styles.spinnerRing} aria-hidden="true" />
        </div>
      )}
      {/* Native <img> (not next/image `fill`) so the photo renders at its
          intrinsic resolution — only scaled DOWN to fit the stage, never up —
          which stops small images from being stretched/pixelated. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        ref={measureRef}
        src={src}
        alt={alt}
        className={styles.image}
        decoding="async"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </>
  );
}

/**
 * A reusable, accessible image-gallery viewer.
 *
 * Features:
 *  - Modal overlay with focus-trapping backdrop + ESC / arrow-key / swipe nav.
 *  - next/image optimized main image with `priority` on the active slide and
 *    an adjacent-neighbor preload pass so prev/next feels instant.
 *  - Horizontal thumbnail strip that auto-scrolls the active thumb into view.
 *  - Mounts via portal; locks body scroll while open.
 *  - Conditional render — when `open` is false, nothing is in the DOM.
 *
 * Designed for performance: heavy DOM is only mounted while the modal is
 * open, neighbor images are warmed but distant images stay lazy.
 */
export default function GalleryViewer({
  open,
  images,
  initialIndex = 0,
  onClose,
  title = "Gallery",
  closeOnBackdropClick = true,
}: GalleryViewerProps) {
  const total = images.length;
  const [mounted, setMounted] = useState(false);
  const [index, setIndex] = useState(() => clampIndex(initialIndex, total));

  const thumbsRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const swipeStartRef = useRef<{ x: number; t: number } | null>(null);

  // SSR-safe portal mounting.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Resync when reopened from a different starting index.
  useEffect(() => {
    if (open) setIndex(clampIndex(initialIndex, total));
  }, [open, initialIndex, total]);

  const next = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goTo = useCallback(
    (i: number) => setIndex(clampIndex(i, total)),
    [total],
  );

  // Keyboard nav + body scroll lock while open.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, next, prev, onClose]);

  // Move focus into the dialog when opened (a11y).
  useEffect(() => {
    if (!open) return;
    stageRef.current?.focus();
  }, [open]);

  // Auto-scroll the active thumbnail into view.
  useEffect(() => {
    if (!open) return;
    const strip = thumbsRef.current;
    if (!strip) return;
    const active = strip.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    active.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [open, index]);

  // Warm the browser cache for the previous & next full-size images so
  // navigation is instant. Distant images stay lazy.
  useEffect(() => {
    if (!open || total < 2) return;
    const neighborSet = new Set<number>([
      (index + 1) % total,
      (index - 1 + total) % total,
    ]);
    const created: HTMLImageElement[] = [];
    neighborSet.forEach((i) => {
      const url = images[i]?.src;
      if (!url) return;
      const img = new window.Image();
      img.decoding = "async";
      img.src = url;
      created.push(img);
    });
    return () => {
      // Drop references so cancelled images aren't held in memory.
      created.length = 0;
    };
  }, [open, index, total, images]);

  // Stable key derivation per slide (used for keys + remounts).
  const keys = useMemo(
    () => images.map((img, i) => img.id ?? `${img.src}#${i}`),
    [images],
  );

  if (!mounted || !open || total === 0) return null;

  const current = images[index];

  const onPointerDown = (e: ReactPointerEvent) => {
    // Mouse drags are noisy on desktop — only treat touch/pen as swipes.
    if (e.pointerType === "mouse") return;
    swipeStartRef.current = { x: e.clientX, t: Date.now() };
  };

  const onPointerUp = (e: ReactPointerEvent) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dt = Date.now() - start.t;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX && dt <= SWIPE_MAX_DURATION_MS) {
      if (dx > 0) prev();
      else next();
    }
  };

  const onBackdropMouseDown = (e: React.MouseEvent) => {
    if (!closeOnBackdropClick) return;
    if (e.target === e.currentTarget) onClose();
  };

  const onStageKey = (e: ReactKeyboardEvent) => {
    // Stage is focusable so screen readers / keyboard users can pick it up;
    // arrow keys are handled by the document listener above.
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      next();
    }
  };

  const view = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={styles.viewer}
      onMouseDown={onBackdropMouseDown}
    >
      <div className={styles.header}>
        <div className={styles.counter}>
          <span className={styles.counterCurrent}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className={styles.counterSep}>/</span>
          <span className={styles.counterTotal}>
            {String(total).padStart(2, "0")}
          </span>
        </div>
        <h2 className={styles.title}>{title}</h2>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Close gallery"
          onClick={onClose}
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      </div>

      <div
        ref={stageRef}
        className={styles.stage}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onKeyDown={onStageKey}
        tabIndex={-1}
      >
        {total > 1 && (
          <button
            type="button"
            className={`${styles.navBtn} ${styles.navPrev}`}
            aria-label="Previous image"
            onClick={prev}
          >
            <i className="fa-solid fa-chevron-left" aria-hidden="true" />
          </button>
        )}

        <div className={styles.imageWrap}>
          {/* Keyed by slide so the element remounts on navigation — fresh
              spinner state and a clean decode of the new photo. */}
          <StageImage
            key={keys[index]}
            src={current.src}
            alt={current.alt}
          />
        </div>

        {total > 1 && (
          <button
            type="button"
            className={`${styles.navBtn} ${styles.navNext}`}
            aria-label="Next image"
            onClick={next}
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        )}
      </div>

      {total > 1 && (
        <div
          ref={thumbsRef}
          className={styles.thumbs}
          role="tablist"
          aria-label="Gallery thumbnails"
        >
          {images.map((img, i) => {
            const isActive = i === index;
            return (
              <button
                key={keys[i]}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Show image ${i + 1}: ${img.alt}`}
                data-active={isActive ? "true" : undefined}
                className={`${styles.thumb} ${isActive ? styles.thumbActive : ""}`}
                onClick={() => goTo(i)}
              >
                <Image
                  src={img.src}
                  alt=""
                  fill
                  sizes="96px"
                  style={{ objectFit: "cover" }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return createPortal(view, document.body);
}
